/**
 * Admin cookie refresh coordination — G4 / v2.3-G2 path denylist + G17 rotate publish.
 *
 * `shouldAttemptRefreshOn401` is a READ-ONLY port of customer
 * `customerRefreshCoordinator.shouldAttemptRefreshOn401`, remapped to admin
 * surface names, plus Locked INCLUDE `/account/step-up/verify` (customer omits
 * step-up). Do not drift the customer implementation from this file.
 *
 * `attemptAdminSessionFromCookies` — same-tab single-flight + XT-G6 cross-tab lock,
 * then POST …/admin|dev/refresh (credentials + CSRF). Dual memory Bearer is forwarded
 * as Authorization + body `priorAccessToken` for Mechanism A′ demo-tour preserve when
 * the access cookie is absent. Soft/hard codes for hooks + ApiClient 401 handler —
 * never ends session here.
 *
 * `validateAdminSessionViaMe` — cookie-first GET `/api/v1/admin/me` (credentials
 * include; optional Bearer). Soft → transient_error; hard/401/403 → revoked for
 * `usePeriodicMeValidation`. stormAwareFetch (no apiClient — circular with 401 handler).
 * G17 — while HTTP_STORM_DETECTED latch is armed, skip /me amplify (429 not transient).
 *
 * Export for the api client 401 handler: when false, skip cookie refresh.
 */

import type { AdminAuthCrossTabScope } from 'pi-kiosk-shared/crossTab';
import { getEnvironmentConfig } from '../../config/environments.js';
import { clearAdminStepUpClientCache } from './adminStepUpClientCache.js';
import { tokenStorage } from '../../services/storage/tokenStorage.js';
import { publishAdminSessionRefreshed } from '../crossTab/adminCrossTab.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';
import {
  isHttpStormLatchedError,
  stormAwareFetch,
} from '../http/stormAwareFetch.js';
import { isAdminPlatformPwaPath } from '../../app/pwa/adminPwaSurfaces.js';
import { buildTenantApiBase, getTenantFromPath, withTenantInPath } from '../tenant.js';
import { adminAuthCsrfHeaders } from './adminAuthCsrf.js';
import {
  AdminRefreshLockTimeoutError,
  withAdminCrossTabRefreshLock,
} from './adminCrossTabRefreshLock.js';
import {
  isHardDefinitiveRefreshRevocation,
  isRefreshSkippedUnderR1,
  isSoftRefreshFailure,
  pendingAdminRefreshSkipResult,
  TOKEN_VERSION_MISMATCH,
} from './adminRefreshCatalog.js';
import { isAdminSessionEndingLatchActive } from './adminSessionEndingLatch.js';
import { isAdminSessionHydrated } from './adminSessionHydratedLatch.js';
import { getAdminSessionSurface } from './endAdminSession.js';

/**
 * Plan G4 TABLE — deny when `path.includes(substring)`.
 * Concrete `/dev/forgot-password` + `/dev/reset-password` (BAN vague `/dev/…`).
 */
export const ADMIN_REFRESH_401_DENY_SUBSTRINGS = [
  '/admin/refresh',
  '/dev/refresh',
  '/admin/logout',
  '/dev/logout',
  '/logout-all',
  '/admin/login',
  '/dev/login',
  '/admin/mfa/verify',
  '/dev/mfa/verify',
  '/admin/forgot-password',
  '/admin/reset-password',
  '/dev/forgot-password',
  '/dev/reset-password',
  '/admin/invite/session',
  '/admin/invite/complete',
  '/admin/oidc/',
  '/dev/oidc/',
  '/admin/events/ticket',
  '/account/step-up/verify',
] as const;

export type AdminRefresh401DenySubstring =
  (typeof ADMIN_REFRESH_401_DENY_SUBSTRINGS)[number];

/**
 * Whether a 401 on `path` should attempt cookie session refresh.
 * Api client 401 handler must call this before refresh — denylisted paths → no attempt.
 */
export function shouldAttemptRefreshOn401(path: string): boolean {
  return !ADMIN_REFRESH_401_DENY_SUBSTRINGS.some((substring) => path.includes(substring));
}

/**
 * BE `UnauthorizedError('No token provided')` — anonymous probe / missing cookie+Bearer.
 * On login pages: stay silent (no refresh thrash). On an established SPA session
 * (see `shouldAttemptRefreshOnMissingCredential`): still attempt cookie rotate —
 * access cookie expiry surfaces as this message while the refresh cookie may live.
 */
export function isMissingCredentialUnauthorized(
  code: string | undefined,
  message: string | undefined,
): boolean {
  const normalized = (message ?? '').trim().toLowerCase();
  if (normalized === 'no token provided') {
    return true;
  }
  // Envelope sometimes puts the same text in `error` while code stays UNAUTHORIZED.
  return code === 'UNAUTHORIZED' && normalized.includes('no token provided');
}

/**
 * Whether a missing-credential 401 should still attempt POST …/refresh.
 * Anonymous cold boot → false. Hydrated cookie session or dual memory Bearer → true.
 */
export function shouldAttemptRefreshOnMissingCredential(): boolean {
  const memoryOrLs = tokenStorage.get();
  if (memoryOrLs !== null && memoryOrLs.length > 0) {
    return true;
  }
  return isAdminSessionHydrated();
}

/** Cookie rotate / hydrate attempt — customer `attemptSessionFromCookies` twin. */
export type AttemptAdminSessionResult =
  | { ok: true; source?: string }
  | { ok: false; code?: string; status?: number };

type AdminRefreshEnvelope = {
  success?: boolean;
  code?: string;
  data?: {
    token?: string;
  };
};

function resolveRefreshSurface(): {
  path: string;
  skipTenantPrefix: boolean;
  scope: AdminAuthCrossTabScope;
  tenantCode: string;
} | null {
  const surface = getAdminSessionSurface();
  if (surface === 'platform') {
    return {
      path: '/api/dev/refresh',
      skipTenantPrefix: true,
      scope: 'platform',
      tenantCode: 'platform',
    };
  }
  // Caller must pending-skip when path tenant is missing — never invent `default`.
  const tenantCode = getTenantFromPath();
  if (tenantCode === null || tenantCode.length === 0) {
    return null;
  }
  return {
    path: '/api/admin/refresh',
    skipTenantPrefix: false,
    scope: 'tenant',
    tenantCode,
  };
}

/**
 * S6/S7 — tenant surface without URL slug (customer R1 `pending_skip` twin).
 * Do not POST refresh against an invented `default` tenant.
 */
export function shouldPendingSkipAdminRefresh(): boolean {
  if (getAdminSessionSurface() !== 'tenant') {
    return false;
  }
  const tenantCode = getTenantFromPath();
  return tenantCode === null || tenantCode.length === 0;
}

/**
 * Dual-mode A′ secondary delivery when the access cookie may be absent.
 * Cookie mode: memory empty → `{}` / no Authorization (A″ cookie carries prior JWT).
 * Dual: memory Bearer present → `Authorization: Bearer` + body `priorAccessToken`
 * so BE `RefreshAdminSession` can preserve demo-tour claims via A′ extract.
 */
export function buildAdminRefreshPriorAccessDelivery(
  priorAccessToken: string | null,
): {
  readonly authorizationHeaders: Record<string, string>;
  readonly body: { readonly priorAccessToken?: string };
} {
  if (priorAccessToken === null || priorAccessToken.length === 0) {
    return { authorizationHeaders: {}, body: {} };
  }
  return {
    authorizationHeaders: { Authorization: `Bearer ${priorAccessToken}` },
    body: { priorAccessToken },
  };
}

/**
 * POST cookie rotate under XT-G6 lock. On success publishes session-refreshed
 * (G17 — notifyLocal false; peer must /me force:false only — refresh POST === 0).
 */
async function postAdminRefreshRotate(): Promise<AttemptAdminSessionResult> {
  // G17 — BAN refresh amplify during HTTP_STORM_DETECTED pause (customer twin).
  if (isHttpStormLatched()) {
    return { ok: false, code: 'HTTP_STORM_LATCHED', status: 429 };
  }

  const resolved = resolveRefreshSurface();
  if (resolved === null) {
    return pendingAdminRefreshSkipResult(tokenStorage.get());
  }
  const { path, skipTenantPrefix, scope, tenantCode } = resolved;
  const config = getEnvironmentConfig();
  const apiBase = buildTenantApiBase(config.apiUrl);
  const endpoint = skipTenantPrefix ? path : withTenantInPath(path);
  const priorDelivery = buildAdminRefreshPriorAccessDelivery(tokenStorage.get());

  let response: Response;
  try {
    response = await stormAwareFetch(`${apiBase}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // Inline UUID — do not import apiClient.core (circular with 401 handler).
        'X-Request-ID': crypto.randomUUID(),
        ...adminAuthCsrfHeaders(),
        ...priorDelivery.authorizationHeaders,
      },
      body: JSON.stringify(priorDelivery.body),
    });
  } catch (err) {
    if (isHttpStormLatchedError(err)) {
      return { ok: false, code: 'HTTP_STORM_LATCHED', status: 429 };
    }
    return { ok: false, code: 'NETWORK_ERROR', status: 0 };
  }

  let envelope: AdminRefreshEnvelope = {};
  try {
    envelope = (await response.json()) as AdminRefreshEnvelope;
  } catch {
    // ignore parse errors
  }

  if (response.ok && envelope.success !== false) {
    // dual MAY emit body JWT for memory Bearer; cookie mode omits (G12).
    const bodyToken = envelope.data?.token;
    if (typeof bodyToken === 'string' && bodyToken.length > 0) {
      tokenStorage.setMemory(bodyToken);
    }
    // G26 — refresh access JWT omits stepUpUntil; drop FE claim so gate re-prompts.
    clearAdminStepUpClientCache();
    // G17 — publish only after successful rotate+Set-Cookie.
    publishAdminSessionRefreshed(tenantCode, scope);
    return { ok: true, source: 'cookie-refresh' };
  }

  const code = typeof envelope.code === 'string' ? envelope.code : undefined;

  // H1 — hard only; surface for hooks/ApiClient to end once.
  if (isHardDefinitiveRefreshRevocation(code)) {
    return { ok: false, code, status: response.status };
  }

  // S5 — soft definitive: preserve TOKEN_VERSION_MISMATCH (do not collapse → S4).
  if (code === TOKEN_VERSION_MISMATCH) {
    return { ok: false, code: TOKEN_VERSION_MISMATCH, status: response.status };
  }

  // S1–S4 / S6–S7 — named soft codes pass through (incl. REFRESH_RACE_LOST when set).
  if (code !== undefined && (isSoftRefreshFailure(code) || isRefreshSkippedUnderR1(code))) {
    return { ok: false, code, status: response.status };
  }

  // S8 — soft residual bare 401: preserve status; do not invent REFRESH_RACE_LOST.
  if (response.status === 401) {
    return { ok: false, code, status: response.status };
  }

  return {
    ok: false,
    code: code ?? 'NETWORK_ERROR',
    status: response.status,
  };
}

/** Same-tab single-flight — customer `inFlightAttempt` twin. */
let inFlightAttempt: Promise<AttemptAdminSessionResult> | null = null;

/**
 * Cookie rotate / hydrate. Same-tab single-flight + XT-G6 lock.
 * Soft lock timeout → REFRESH_LOCK_TIMEOUT (no unlocked dual-run).
 * Soft race / soft codes never publish logout. Hard codes return for hooks/ApiClient to end once.
 */
export async function attemptAdminSessionFromCookies(opts?: {
  force?: boolean;
  source?: string;
}): Promise<AttemptAdminSessionResult> {
  const source = opts?.source ?? 'cookie-refresh';

  // S6/S7 live producer — before lock/HTTP (customer pending_skip twin).
  if (shouldPendingSkipAdminRefresh()) {
    return pendingAdminRefreshSkipResult(tokenStorage.get());
  }

  if (inFlightAttempt !== null) {
    return inFlightAttempt;
  }

  const promise = (async (): Promise<AttemptAdminSessionResult> => {
    try {
      const result = await withAdminCrossTabRefreshLock(async () => postAdminRefreshRotate());
      if (result.ok) {
        return { ok: true, source };
      }
      return result;
    } catch (err) {
      if (err instanceof AdminRefreshLockTimeoutError) {
        return { ok: false, code: 'REFRESH_LOCK_TIMEOUT', status: 0 };
      }
      return { ok: false, code: 'NETWORK_ERROR', status: 0 };
    }
  })().finally(() => {
    if (inFlightAttempt === promise) {
      inFlightAttempt = null;
    }
  });

  inFlightAttempt = promise;
  return promise;
}

export type AdminMeValidationResult =
  | { status: 'ok' }
  | { status: 'revoked' }
  | { status: 'transient_error' };

/** Customer twin — brief backoff on 0/429/503/504 before soft-stop. */
const ME_RETRY_DELAYS_MS = [400, 900] as const;

type AdminMeEnvelope = {
  success?: boolean;
  code?: string;
  data?: { user?: { id?: number } };
};

function isTransientMeStatus(status: number): boolean {
  // G17 — while HTTP_STORM_DETECTED latch is armed, 429 must not retry /me
  // (amplification SoT). Other transient statuses keep retrying.
  if (status === 429 && isHttpStormLatched()) {
    return false;
  }
  return status === 0 || status === 503 || status === 504 || status === 429;
}

/**
 * Resolve GET /me path — AuthProvider / `apiClient.getCurrentUser` twin.
 * Live BE route is always `/api/v1/admin/me` (no `/api/dev/me`).
 * G3: skip tenant prefix only on `/dev*` (R-GAP-01 platform default-allow).
 * Platform sessions on `/{tenant}/...` bind the explored tenant — do not use
 * logout-surface, which stays 'platform' after /dev/login.
 */
function resolveMeSurface(): { path: string; skipTenantPrefix: boolean } {
  if (typeof window !== 'undefined' && isAdminPlatformPwaPath(window.location.pathname)) {
    return { path: '/api/v1/admin/me', skipTenantPrefix: true };
  }
  return { path: '/api/v1/admin/me', skipTenantPrefix: false };
}

function mapMeFailureToValidation(
  status: number,
  code: string | undefined,
): AdminMeValidationResult {
  // H1 — definitive revoke codes end session after refresh arm in the hook.
  if (isHardDefinitiveRefreshRevocation(code)) {
    return { status: 'revoked' };
  }
  // Soft catalog (S1–S4/S6/S7) — never treat as revoke; next interval retries.
  // Do not fold S5/S8 into this branch (TVM + bare 401 are soft-until-recovery).
  if (isSoftRefreshFailure(code) || isRefreshSkippedUnderR1(code)) {
    return { status: 'transient_error' };
  }
  // S5 — soft definitive: surface as revoked so the hook can attempt cookie
  // rotate once, then soft-stop on residual (never hard on first mismatch).
  if (code === TOKEN_VERSION_MISMATCH) {
    return { status: 'revoked' };
  }
  // S8 / auth residual — hook may rotate once; bare 401 ≠ invent S4.
  if (status === 401 || status === 403) {
    return { status: 'revoked' };
  }
  return { status: 'transient_error' };
}

/** Same-tab single-flight for periodic /me — customer `inFlightMe` twin. */
const inFlightMe = new Map<string, Promise<AdminMeValidationResult>>();

/**
 * Periodic cookie-first `/me` validation — customer `validateSessionViaMe` twin.
 * stormAwareFetch (no apiClient — circular with 401 handler). Soft/hard codes mapped
 * for `usePeriodicMeValidation` (soft → transient; hard/401/403 → revoked).
 * G17 — soft-skip while storm latch is armed (no /me stampede under pause).
 */
export async function validateAdminSessionViaMe(
  adminUserId: number,
  accessToken: string | null,
): Promise<AdminMeValidationResult> {
  if (isAdminSessionEndingLatchActive()) {
    return { status: 'transient_error' };
  }

  const surface = getAdminSessionSurface();
  const key = `${surface}:${adminUserId}`;
  const pending = inFlightMe.get(key);
  if (pending !== undefined) {
    return pending;
  }

  const promise = (async (): Promise<AdminMeValidationResult> => {
    // G17 — soft-skip coordinator /me while storm latch is armed.
    if (isHttpStormLatched()) {
      return { status: 'transient_error' };
    }

    const { path, skipTenantPrefix } = resolveMeSurface();
    const config = getEnvironmentConfig();
    const apiBase = buildTenantApiBase(config.apiUrl);
    const endpoint = skipTenantPrefix ? path : withTenantInPath(path);
    const bearer =
      accessToken !== null && accessToken.length > 0
        ? accessToken
        : tokenStorage.get();

    for (let attempt = 0; attempt <= ME_RETRY_DELAYS_MS.length; attempt += 1) {
      let response: Response;
      try {
        if (isHttpStormLatched()) {
          return { status: 'transient_error' };
        }
        response = await stormAwareFetch(`${apiBase}${endpoint}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            // Inline UUID — do not import apiClient.core (circular with 401 handler).
            'X-Request-ID': crypto.randomUUID(),
            ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          },
        });
      } catch (err) {
        if (isHttpStormLatchedError(err) || isHttpStormLatched()) {
          return { status: 'transient_error' };
        }
        if (attempt < ME_RETRY_DELAYS_MS.length) {
          await new Promise((r) => setTimeout(r, ME_RETRY_DELAYS_MS[attempt]));
          continue;
        }
        return { status: 'transient_error' };
      }

      if (isTransientMeStatus(response.status)) {
        if (attempt < ME_RETRY_DELAYS_MS.length) {
          await new Promise((r) => setTimeout(r, ME_RETRY_DELAYS_MS[attempt]));
          continue;
        }
        return { status: 'transient_error' };
      }

      let envelope: AdminMeEnvelope = {};
      try {
        envelope = (await response.json()) as AdminMeEnvelope;
      } catch {
        // ignore parse errors
      }

      const code = typeof envelope.code === 'string' ? envelope.code : undefined;

      if (response.ok && envelope.success !== false) {
        const meId = envelope.data?.user?.id;
        // Identity drift → treat as revoke so hook can rotate / hard-end once.
        if (typeof meId === 'number' && meId !== adminUserId) {
          return { status: 'revoked' };
        }
        return { status: 'ok' };
      }

      return mapMeFailureToValidation(response.status, code);
    }

    return { status: 'transient_error' };
  })().finally(() => {
    inFlightMe.delete(key);
  });

  inFlightMe.set(key, promise);
  return promise;
}
